import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import AppShell, { ItemNavegacao } from '../../ui/composicao/AppShell';
import SinoNovidades from '../sino-novidades/SinoNovidades';
import { NomeIcone } from '../../ui/primitivos/Icone';
import { RootState } from '../../store/store';
import { clearCotacaoData } from '../../store/cotacaoSlice';
import { iniciarNovaAdesao } from '../../utils/adesaoFluxo';
import { performLogout } from '../../utils/authLogout';
import { decodeJwtPayload } from '../../utils/jwt';
import { idOpcaoAtiva, OpcaoMenu, opcoesVisiveis } from '../../views/home/menuOpcoes';
import { HOME } from '../../views/home/routes';

/**
 * Ponte entre o `AppShell` (puro, em `src/ui`) e o domínio do SAAC (at-55).
 *
 * Existe porque `src/ui` não pode importar de `store/`, `api/` ou `views/` — é
 * essa regra de camada que mantém o design system reutilizável. Todo o
 * conhecimento de negócio da navegação mora AQUI: quais opções o papel enxerga,
 * o que acontece ao entrar em cada uma, e como se desloga.
 *
 * ⚠️ As opções vêm de `views/home/menuOpcoes.ts` — a MESMA lista da Home e do
 * `NavGlobal` legado. Nunca duplicar essa lista.
 */

/** `id` de `menuOpcoes.ts` → ícone do design system. */
const ICONE_POR_OPCAO: Record<string, NomeIcone> = {
  cotacoes: 'cotacoes',
  adesoes: 'adesoes',
  'substituicao-veicular': 'substituicao-veicular',
  'substituicao-titular': 'substituicao-titular',
  vistoria: 'vistoria',
  carteira: 'carteira',
  'acompanhamento-oficina': 'acompanhamento-oficina',
  ti: 'ti',
};

/**
 * "Voltar" do cabeçalho, com a regra decidida por Victor em 01/09/2026: volta
 * pela tela ANTERIOR (histórico), não por um endereço fixo.
 *
 * O React Router guarda o índice da entrada atual em `history.state.idx`;
 * `idx > 0` garante que existe tela anterior DENTRO do app. Sem essa checagem,
 * um `navigate(-1)` em aba nova ou link direto jogaria o usuário para fora do
 * SAAC (ou para o login, num refresh).
 */
export function useVoltar(destinoDeFallback?: string): () => void {
  const navigate = useNavigate();

  return () => {
    const indice = (window.history.state as { idx?: number } | null)?.idx;
    if (typeof indice === 'number' && indice > 0) {
      navigate(-1);
      return;
    }
    navigate(destinoDeFallback ?? HOME());
  };
}

interface PropsShellSaac {
  cabecalho?: React.ReactNode;
  children: React.ReactNode;
}

const ShellSaac: React.FC<PropsShellSaac> = ({ cabecalho, children }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const userType = useSelector((state: RootState) => state.auth.userType);
  const accessToken = useSelector((state: RootState) => state.auth.accessToken);

  // O nome do usuário vive só no JWT — o `authSlice` não o guarda.
  const nome = useMemo(() => {
    if (!accessToken) return null;
    const payload = decodeJwtPayload(accessToken) as { nome?: string } | null;
    return payload?.nome ?? null;
  }, [accessToken]);

  const opcoes = useMemo(() => opcoesVisiveis(userType), [userType]);
  const idAtivo = useMemo(() => idOpcaoAtiva(opcoes, location.pathname), [opcoes, location.pathname]);
  const inicioAtivo = location.pathname === HOME() || location.pathname === '/';

  /**
   * Efeitos colaterais de navegação — contrato de `menuOpcoes.ts`.
   * ⚠️ Valem aqui E na Home: tratar só num dos dois faria o começo do fluxo
   * depender de por onde o consultor entrou (foi bug real da at-38).
   */
  const abrir = (opcao: OpcaoMenu) => {
    if (opcao.limparCotacao) dispatch(clearCotacaoData());
    if (opcao.limparAdesao) iniciarNovaAdesao(dispatch);
    navigate(opcao.rota);
  };

  const itens: ItemNavegacao[] = opcoes.map((opcao) => ({
    id: opcao.id,
    titulo: opcao.titulo,
    icone: ICONE_POR_OPCAO[opcao.id] ?? 'carteira',
    ativo: opcao.id === idAtivo,
    aoSelecionar: () => abrir(opcao),
  }));

  return (
    <AppShell
      itens={itens}
      inicioAtivo={inicioAtivo}
      aoIrParaInicio={() => navigate(HOME())}
      /* Logout manual vai sem motivo — o aviso na tela de login é só para
         sessão encerrada pelo servidor (at-22). */
      aoSair={() => void performLogout()}
      usuario={{ nome, papel: userType }}
      /*
       * Logo QUADRADA da marca (`public/atual_logo.png`): o símbolo do ticket em
       * branco sobre o quadrado laranja arredondado — a mesma arte do ícone do app
       * e do apple-touch-icon (spec 01 §2.3).
       *
       * ⚠️ Ela já É o quadrado laranja, então não precisa de nenhum invólucro
       * colorido em volta. Antes havia aqui um quadrado laranja desenhado à mão com
       * o ícone de MALETA dentro — placeholder meu que passava por marca.
       *
       * Vem de `public/` e não de `src/img/` porque é o mesmo arquivo que o
       * `index.html` já serve como favicon: duplicar em `src/` criaria duas cópias
       * da marca para manter em sincronia.
       */
      marca={
        <>
          <img
            src={`${process.env.PUBLIC_URL}/atual_logo.png`}
            alt="Atual Proteção Veicular"
            className="tw-h-10 tw-w-10 tw-shrink-0 tw-rounded-xl"
          />
          {/* O símbolo sozinho não diz o nome da marca — `alt` serve o leitor de
              tela, não quem enxerga. `aria-hidden` para o nome não ser lido duas
              vezes, já que o `alt` da imagem acima já o anuncia. */}
          <span className="tw-flex tw-flex-col tw-leading-none" aria-hidden="true">
            <span className="tw-font-marca tw-text-lg tw-font-bold tw-text-white">atual</span>
            <span className="tw-mt-0.5 tw-text-[9px] tw-font-semibold tw-uppercase tw-tracking-widest tw-text-white/40">
              Proteção Veicular
            </span>
          </span>
        </>
      }
      cabecalho={cabecalho}
      /*
       * O sino vem para DENTRO do cabeçalho nas rotas migradas (at-56).
       *
       * 🔴 Flutuando (`position: fixed; right: 16px`) ele caía **por cima** do
       * botão da tela — o "Nova Adesão +" de `/adesoes`, visto por Victor em
       * 09/09/2026. Deslocar o `right` não resolveria: quebraria na próxima tela
       * com um botão de largura diferente.
       *
       * ⚠️ O `NavGlobal` **não** o renderiza nestas rotas (`rotasComShell`),
       * senão haveria dois sinos — o mesmo cuidado que ele já tem com o
       * hambúrguer. É a mudança que o `13-ui-kit.md` previa para quando as rotas
       * migradas cobrissem o app; começou aqui, por necessidade.
       */
      acoesGlobais={<SinoNovidades embutido />}
    >
      {children}
    </AppShell>
  );
};

export default ShellSaac;
